import React, { useCallback, useEffect, useRef, useState } from "react";
import { TableContainer, type TableContainerProps } from "@chakra-ui/react";

export interface DataTableProps extends TableContainerProps {
  /** Forwarded to the child <Table> when it has no size of its own. */
  size?: "sm" | "md";
  /** A single <Table> element. */
  children: React.ReactNode;
  /** Accessible name for the scroll region when the table overflows (it then becomes keyboard-focusable). */
  label?: string;
}

/**
 * Bordered, rounded table container with horizontal scroll and a right-edge fade that appears only while the table
 * is wider than the viewport and not scrolled to the end (measured with ResizeObserver).
 * @example <DataTable size="sm"><Table><Thead>…</Thead><Tbody>…</Tbody></Table></DataTable>
 */
export const DataTable: React.FC<DataTableProps> = ({ size, children, label = "Table", ...rest }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setScrollable(el.scrollWidth > el.clientWidth + 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const table = el.firstElementChild;
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, [measure]);

  // Re-measure after every render (rows may change without a resize event) without re-subscribing the observer.
  useEffect(() => {
    measure();
  });

  const child =
    size && React.isValidElement(children) && (children.props as { size?: string }).size === undefined
      ? React.cloneElement(children as React.ReactElement<{ size?: string }>, { size })
      : children;

  return (
    <TableContainer
      ref={ref}
      onScroll={measure}
      // Overflowing tables must be scrollable from the keyboard (axe scrollable-region-focusable), as in WizardProgress.
      tabIndex={scrollable ? 0 : undefined}
      role={scrollable ? "region" : undefined}
      aria-label={scrollable ? label : undefined}
      _focusVisible={{ boxShadow: "outline", outline: "none" }}
      data-scrollable={scrollable && !atEnd ? "true" : undefined}
      border="1px solid"
      borderColor="border.default"
      borderRadius="md"
      bg="surface.card"
      overflowX="auto"
      overflowY="hidden"
      sx={{
        "&[data-scrollable=true]": {
          maskImage: "linear-gradient(to right, #000 calc(100% - 24px), transparent)",
          WebkitMaskImage: "linear-gradient(to right, #000 calc(100% - 24px), transparent)",
        },
        // A mask clips everything outside the border box, including the focus ring: drop it while focused.
        "&[data-scrollable=true]:focus-visible": { maskImage: "none", WebkitMaskImage: "none" },
      }}
      {...rest}
    >
      {child}
    </TableContainer>
  );
};
